import { Languages, LocalStorageKeys, PageTypeSuffix } from '@/constants'
import { useNotification, useStore } from '@/containers'
import { useDebounce } from '@/hooks/useDebounce'
import type { Category, Product } from '@/types'
import { api } from '@/utils'
import { getTranslate } from '@/utils/api-interceptors'
import { getCategories, getProductWithPaginate } from '@/utils/endpoint'
import { useClickOutsideComponent } from '@/utils/useClickOutsideComponent'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import SearchClearIcon from '../Icon/SearchClearIcon'
import SearchIcon from '../Icon/SearchIcon'
import { SearchProductItemCard } from '../SearchProductItemCard'

export const SearchBar = () => {
  const maxItemsReached = 3
  const t = useTranslations('Header')
  const { systemConfig } = useNotification()
  const [searchBarValue, setSearchBarValue] = useState('')
  const { ref, isComponentVisible, setIsComponentVisible } =
    useClickOutsideComponent(false)
  const router = useRouter()
  const debouncedSearchBarValue = useDebounce(searchBarValue, 500)
  const { keywords } = useStore()
  const [displayResult, setDisplayResult] = useState<{
    products: Product[] | null | undefined
    categories: Category[] | null | undefined
  } | null>(null)

  const handleInputKeyUp = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      if (searchBarValue === '') {
        localStorage?.removeItem(LocalStorageKeys.ITEM_LISTING_SEARCH)
      }
    }
    if (event.key === 'Enter') {
      const res = await api(`api/keyword-cat?keyword=${searchBarValue}`, {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
      })
      if (res && !(res instanceof Error) && res.data) {
        router.push(
          getTranslate(res?.data?.slugs?.value) +
            PageTypeSuffix.CATEGORY +
            `?query=${searchBarValue}`
        )
      } else {
        return router.push(`/search?query=${searchBarValue}`)
      }
    }
  }

  useEffect(() => {
    if (router.query && router.query.query) {
      setSearchBarValue(router.query.query as string)
    }
  }, [router.query])

  useEffect(() => {
    const query = router.query
    if (debouncedSearchBarValue && router.query) {
      Promise.all([
        getProductWithPaginate({
          ...query,
          fullTextSearch: debouncedSearchBarValue,
          limit: maxItemsReached,
          sortBy: 'score',
          sortType: 'desc',
        }).then(res => res?.data),
        getCategories({
          ...query,
          fullTextSearch: debouncedSearchBarValue,
          limit: maxItemsReached,
          sortBy: 'score',
          sortType: 'desc',
        }).then(res => res?.data),
      ]).then(res => {
        const [products, categories] = res
        setDisplayResult({
          products,
          categories,
        })
      })
    } else {
      setDisplayResult(null)
    }
  }, [debouncedSearchBarValue])

  const EmptyMessage = () => (
    <h3 className='EmptyMessage flex h-12 w-full items-center bg-grey-200 pl-2 text-Headline text-dark-400'>
      {t('searching')}
    </h3>
  )

  const searchInputRef = useRef<HTMLInputElement>(null)

  return (
    <div>
      <div className='relative flex h-12 w-full items-center gap-2 rounded-full bg-white p-2'>
        <input
          ref={searchInputRef}
          type='text'
          className='w-full pl-3 pr-5'
          placeholder={t('searchPld', {
            brand: systemConfig?.websiteBrandName,
          })}
          value={searchBarValue}
          onChange={e => {
            setSearchBarValue(e.target.value)
            setIsComponentVisible(true)
          }}
          onKeyUp={e => handleInputKeyUp(e)}
        />
        {!!searchBarValue && (
          <button
            onClick={() => {
              setSearchBarValue('')
              searchInputRef.current?.focus()
            }}
          >
            <SearchClearIcon />
          </button>
        )}
        <button
          className='rounded-full bg-[#003dd41a] p-2'
          onClick={() => {
            if (searchBarValue !== '')
              return router.push(`/search?query=${searchBarValue}`)
          }}
        >
          <SearchIcon />
        </button>

        {/* RESULTS */}
        {isComponentVisible && searchBarValue.length > 0 && (
          <div
            className='absolute left-0 top-0 z-[999] mt-14 w-full rounded bg-white-400 shadow-grey-400 drop-shadow'
            ref={ref}
          >
            {displayResult ? (
              <>
                <div className='searchProductResult w-full'>
                  <h3 className='searchProductResultHeader flex h-12 w-full items-center bg-grey-200 pl-2 text-Headline text-dark-400'>
                    {t('productSuggest')}
                  </h3>
                  <div className='searchProductResultContent grid'>
                    {displayResult.products?.map(el => (
                      <Link
                        onClick={() => setIsComponentVisible(false)}
                        key={el._id}
                        href={
                          '/' + getTranslate(el.slugs.value) + el.slugs.postfix
                        }
                        className='block'
                      >
                        <SearchProductItemCard
                          id={el._id}
                          price={el.finalPrice
                            .toLocaleString(Languages.VN, {
                              style: 'currency',
                              currency: 'VND',
                            })
                            .replace(/\s₫/g, 'đ')}
                          name={getTranslate(el.name)}
                          imgSrc={el?.thumbnail}
                          imgAlt={getTranslate(el.name)}
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <EmptyMessage />
              </>
            )}
          </div>
        )}
      </div>
      {!!keywords.length && (
        <div className='mt-2'>
          <div className='line-clamp-1 w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm text-white'>
            {keywords.map(item => (
              <Link
                key={item._id}
                href={`/search?query=${encodeURIComponent(
                  getTranslate(item.name)
                )}`}
                className='mr-4'
              >
                {getTranslate(item.name)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
